import { getRoot, Instance, types } from 'mobx-state-tree';
import { PageModel, PageRoutes, RootType } from '../../../internal';
import { QuizAnswerApiItem, quizImageUrl } from '../../../utils/api/QuizApi';

export const KreirajOdgovorModel = types
  .compose(
    PageModel,
    types.model({
      answerText: types.optional(types.string, ''),
      isCorrect: types.optional(types.boolean, false),
      orderNumber: types.optional(types.number, 0),
      questionText: types.optional(types.string, ''),
      questionImage: types.optional(types.maybeNull(types.string), null),
    })
  )
  .views((self) => ({
    get root(): RootType {
      return getRoot(self);
    },
    get questionImageUrl(): string {
      return quizImageUrl(self.questionImage);
    },
  }))
  .actions((self) => ({
    setAnswerText(value: string) {
      self.answerText = value;
    },
    setIsCorrect(value: boolean) {
      self.isCorrect = value;
    },
    setOrderNumber(value: number) {
      self.orderNumber = value;
    },
    setQuestion(data: { questionText: string; image?: string | null }) {
      self.questionText = data.questionText;
      self.questionImage = data.image ?? null;
    },
    async beforePageEnter() {
      self.answerText = '';
      self.isCorrect = false;
      self.orderNumber = 0;
      self.questionText = '';
      self.questionImage = null;
      const { quizId, questionId } = self.root.router.queryParams;
      try {
        const question = await self.root.api.quiz.getQuestion(
          Number(quizId),
          Number(questionId)
        );
        this.setQuestion(question);
      } catch {
        // Question context is supplementary — a failed fetch must not block
        // adding answers, so we silently fall back to no context.
      }
      return Promise.resolve(true);
    },
    navigateBack() {
      const { quizId, questionId } = self.root.router.queryParams;
      self.root.router.navigate({
        newView: PageRoutes.Odgovori.id,
        queryParams: { quizId, questionId },
      });
    },
    async submit() {
      const { quizId, questionId } = self.root.router.queryParams;
      try {
        if (self.isCorrect) {
          const existing = await self.root.api.quiz.getAnswers(
            Number(quizId),
            Number(questionId)
          );
          await Promise.all(
            existing
              .filter((a: QuizAnswerApiItem) => a.isCorrect)
              .map((a: QuizAnswerApiItem) =>
                self.root.api.quiz.updateAnswer(
                  Number(quizId),
                  Number(questionId),
                  a.id,
                  { isCorrect: false }
                )
              )
          );
        }
        await self.root.api.quiz.createAnswer(
          Number(quizId),
          Number(questionId),
          {
            answerText: self.answerText,
            isCorrect: self.isCorrect,
            orderNumber: self.orderNumber,
          }
        );
        this.navigateBack();
      } catch (err: any) {
        const message =
          err?.response?.data?.message ??
          'Došlo je do greške pri čuvanju odgovora.';
        self.root.showFailure(message, null);
      }
    },
  }))
  .named('KreirajOdgovorModel');

export type KreirajOdgovorModelType = Instance<typeof KreirajOdgovorModel>;
